import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AiMessage, ApprovalRequest } from '@comp-sys/shared';

export interface ToolCallEntry {
  id: string;
  name: string;
  label: string;
  status: 'calling' | 'done' | 'error';
  result?: unknown;
  error?: string;
  params?: Record<string, unknown>;
  description?: string;
}

export interface StreamingTextBlock {
  type: 'text';
  id: string;
  content: string;
}

export type StreamingBlock = StreamingTextBlock | ({ type: 'tool_call' } & ToolCallEntry);

interface AiChatState {
  isOpen: boolean;
  isFloating: boolean;
  isConversationListOpen: boolean;
  activeConversationId: string | null;
  messages: AiMessage[];
  conversationTitle: string | null;
  isStreaming: boolean;
  streamingBlocks: StreamingBlock[];
  pendingApprovals: Record<string, ApprovalRequest>;
  panelWidth: number;
  autoApprove: boolean;

  setOpen: (open: boolean) => void;
  toggleOpen: () => void;
  setFloating: (floating: boolean) => void;
  setConversationListOpen: (open: boolean) => void;
  setActiveConversation: (id: string | null) => void;
  setMessages: (messages: AiMessage[]) => void;
  addMessage: (message: AiMessage) => void;
  setStreaming: (streaming: boolean) => void;
  appendStreamingText: (text: string) => void;
  addStreamingToolCall: (entry: ToolCallEntry) => void;
  updateStreamingToolCall: (name: string, update: Partial<ToolCallEntry>) => void;
  addApproval: (approval: ApprovalRequest) => void;
  removeApproval: (requestId: string) => void;
  setPanelWidth: (width: number) => void;
  setAutoApprove: (autoApprove: boolean) => void;
  reset: () => void;
}

export const useAiChatStore = create<AiChatState>()(
  persist(
    (set) => ({
      isOpen: false,
      isFloating: false,
      isConversationListOpen: false,
      activeConversationId: null,
      messages: [],
      conversationTitle: null,
      isStreaming: false,
      streamingBlocks: [],
      pendingApprovals: {},
      panelWidth: 384,
      autoApprove: false,

      setOpen: (open) => set({ isOpen: open }),
      toggleOpen: () => set((state) => ({ isOpen: !state.isOpen })),
      setFloating: (floating) => set({ isFloating: floating }),
      setConversationListOpen: (open) => set({ isConversationListOpen: open }),
      setActiveConversation: (id) => set({ activeConversationId: id }),
      setMessages: (messages) => set({ messages }),
      addMessage: (message) => set((state) => ({ messages: [...state.messages, message] })),
      setStreaming: (streaming) => set({
        isStreaming: streaming,
        streamingBlocks: [],
      }),
      appendStreamingText: (text) => set((state) => {
        const blocks = state.streamingBlocks;
        const last = blocks[blocks.length - 1];
        if (last && last.type === 'text') {
          return { streamingBlocks: [...blocks.slice(0, -1), { ...last, content: last.content + text }] };
        }
        return { streamingBlocks: [...blocks, { type: 'text' as const, id: `st_${Date.now()}`, content: text }] };
      }),
      addStreamingToolCall: (entry) => set((state) => ({
        streamingBlocks: [...state.streamingBlocks, { type: 'tool_call' as const, ...entry }],
      })),
      updateStreamingToolCall: (name, update) => set((state) => {
        const idx = state.streamingBlocks.findIndex(
          (b) => b.type === 'tool_call' && b.name === name && b.status === 'calling',
        );
        if (idx === -1) return {};
        const updated = [...state.streamingBlocks];
        updated[idx] = { ...updated[idx], ...update } as StreamingBlock;
        return { streamingBlocks: updated };
      }),
      addApproval: (approval) => set((state) => ({
        pendingApprovals: { ...state.pendingApprovals, [approval.requestId]: approval },
      })),
      removeApproval: (requestId) => set((state) => {
        const { [requestId]: _, ...rest } = state.pendingApprovals;
        return { pendingApprovals: rest };
      }),
      setPanelWidth: (width) => set({ panelWidth: Math.max(320, Math.min(640, width)) }),
      setAutoApprove: (autoApprove) => set({ autoApprove }),
      reset: () => set({
        activeConversationId: null,
        messages: [],
        conversationTitle: null,
        isStreaming: false,
        streamingBlocks: [],
        pendingApprovals: {},
      }),
    }),
    {
      name: 'ai-chat-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isOpen: state.isOpen,
        isFloating: state.isFloating,
        panelWidth: state.panelWidth,
        autoApprove: state.autoApprove,
      }),
    },
  ),
);
